export const DesignSystemOptions = {
  extends: 'SettingsSection',
  state: 'designSystem',
  Title: {
    text: '{{ editor.ds.settingsTitle | polyglot }}',
    QuestionMarkTooltip: {
      onClick: (ev, el, s) => {
        s.update(
          {
            useReset: true,
            useVariable: true,
            useDocumentTheme: true,
            useFontImport: true,
            useIconSprite: true,
            useSvgSprite: true,
            useDefaultConfig: true,
            verbose: false
          },
          {
            preventShModalUpdate: true
          }
        )
      }
    }
  },
  Grid: {
    childExtends: {
      extends: 'OptionToggle',
      onChange: async (ev, el, s) => {
        const { key } = el
        const val = s[key]
        await el.updateDesignSystem(null, key, !val, {
          message: `Set ${key} design system settings to ${!val}`
        })
        el.call('setCookie', key, !val)
      },
      Input: {
        checked: (el) => el.getDesignSystem()[el.parent.key]
      }
    },
    children: [
      {
        key: 'useReset',
        QuestionMarkTooltip: {
          TooltipHidden: {
            shapeDirection: 'top',
            tip: 'Reset CSS browser defaults and matching Symbols Design System'
          }
        },
        Caption: {
          text: '{{ editor.ds.normalize | polyglot }}'
        }
      },
      {
        key: 'useVariable',
        QuestionMarkTooltip: {
          TooltipHidden: {
            shapeDirection: 'top',
            tip: 'Apply CSS values as variables'
          }
        },
        Caption: {
          text: '{{ editor.ds.cssVars | polyglot }}'
        }
      },
      {
        key: 'useDocumentTheme',
        QuestionMarkTooltip: {
          TooltipHidden: {
            shapeDirection: 'top',
            tip: 'Apply global @dark @light, and other themes to the document'
          }
        },
        Caption: {
          text: '{{ editor.ds.globalThemes | polyglot }}'
        }
      },
      {
        key: 'useFontImport',
        QuestionMarkTooltip: {
          TooltipHidden: {
            shapeDirection: 'top',
            tip: 'Import fonts via @font-face from Symbols cdn'
          }
        },
        Caption: {
          text: '{{ editor.ds.importFonts | polyglot }}'
        }
      },
      {
        key: 'useIconSprite',
        QuestionMarkTooltip: {
          TooltipHidden: {
            shapeDirection: 'top',
            tip: 'Bundle icons as SVG sprites'
          }
        },
        Caption: {
          text: '{{ editor.ds.spriteIcons | polyglot }}'
        }
      },
      {
        key: 'useSvgSprite',
        QuestionMarkTooltip: {
          TooltipHidden: {
            shapeDirection: 'top',
            tip: 'Bundle SVGs as SVG sprites'
          }
        },
        Caption: {
          text: '{{ editor.ds.spriteSvg | polyglot }}'
        }
      },
      {
        key: 'useDefaultConfig',
        QuestionMarkTooltip: {
          TooltipHidden: {
            shapeDirection: 'top',
            tip: 'Use default config as fallback for components'
          }
        },
        Caption: {
          text: '{{ editor.ds.defaultConfig | polyglot }}'
        }
      },
      {
        key: 'verbose',
        QuestionMarkTooltip: {
          TooltipHidden: {
            shapeDirection: 'top',
            tip: 'Design System verbose mode in console'
          }
        },
        Caption: {
          text: '{{ editor.ds.verbose | polyglot }}'
        }
      },
      {
        extends: 'SelectDropdownWithTitle',
        ignoreChildExtends: true,
        // Row-separator-as-chrome (design-direction.md §8 / DESIGN_LANGUAGE
        // §2.5) — was a top border split across '@dark'/'@light' with raw
        // `--color-line-dark`/`--color-line-light` custom-property names (not
        // even wrapped in a token). The row's own `padding: 'B1 - -'` top
        // padding already separates it from the toggle grid above.
        padding: 'B1 - -',
        Title: {
          opacity: '.65',
          // Was `inactive` — same fix as SwitchFieldWithCaption.Caption
          // (company/packages/brand): composited with this row's `.65`
          // opacity it measured 2.98:1 light / 3.49:1 dark (needs 4.5; AA
          // walk 2026-08-09, LMC-4). `caption` holds >=4.6 light / >=5.7
          // dark at the same opacity.
          color: 'caption',
          text: '{{ settings.platform.defaultTheme | polyglot }}'
        },
        SelectDropdown: {
          Value: {
            Text: {
              text: (el) =>
                el.call('toDescriptionCase', el.getDesignSystem('globalTheme')) || 'Auto'
            }
          },
          Dropdown: {
            top: '121%',
            options: [
              {
                key: 'auto',
                labelKey: 'settings.platform.theme.auto'
              },
              {
                key: 'dark',
                labelKey: 'settings.platform.theme.dark'
              },
              {
                key: 'light',
                labelKey: 'settings.platform.theme.light'
              },
              {
                key: 'custom',
                labelKey: 'editor.canvasBg.custom'
              }
            ],
            onChoose: async (ev, el, s, ctx, calleeElement) => {
              const globalTheme = calleeElement.state.key

              this.sdk.updateData([['update', ['designSystem', 'globaltheme'], globalTheme]], {
                message: `Updating globalTheme to ${globalTheme}`
              })

              el.getRootState().update({
                globalTheme
              })

              el.call('setCookie', 'globalTheme', el.getDesignSystem('globalTheme'))
            },
            DropdownHeader: {
              text: '{{ settings.platform.globalTheme | polyglot }}'
            },
            ListInDropdown: {
              childExtends: {
                extends: 'KeyValueColumnFields',
                isActive: (el, s) => el.getDesignSystem('globalTheme') === s.key,
                // NO `text:` here. KeyValueColumnFields already renders the
                // option label in its own `Text` slot (`s.text || s.value ||
                // s.key`), and this row HAS children, so a `text:` on the row
                // adds a SECOND text node beside them: every global theme read
                // "dark ✓ dark". The row is a chassis with slots, not a leaf.
                // (Same defect and same fix as LanguageSelectField.js.)
                fontWeight: '400',
                // The options carry polyglot KEYS (labelKey): a '{{ … | polyglot }}'
                // string read out of row state is data to the template resolver since
                // smbls 3c416df0c, and this Text slot painted raw 'Auto / Dark / Light'.
                Text: { text: (el, s) => el.call('polyglot', s.labelKey) }
              }
            }
          }
        }
      }
    ],
    childrenAs: 'props'
  }
}
